import { defineMessages, type IntlShape } from "react-intl";

export { getBrandAvatarStyle, getBrandInitials, type BrandAvatarStyle } from "@runcheetah/shared";

export type BrandSwitcherStatus = "queued" | "processing" | "scanned" | "failed";

export type BrandSwitcherBrand = {
  sourceDomain: string;
  status: BrandSwitcherStatus;
  profileName?: string | null;
  tagline?: string | null;
  logoUrl?: string | null;
  colorPalette?: string[] | null;
  secondaryColorPalette?: string[] | null;
  typographyStyle?: unknown;
};

export type BrandSwitcherBrandWithId = BrandSwitcherBrand & {
  id: string;
};

const trimToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const brandSwitcherStatusMessages = defineMessages({
  queued: {
    id: "brandSwitcher.status.queued",
    defaultMessage: "Queued",
  },
  processing: {
    id: "brandSwitcher.status.processing",
    defaultMessage: "Processing",
  },
  failed: {
    id: "brandSwitcher.status.failed",
    defaultMessage: "Failed",
  },
});

export const getBrandSwitcherTitle = (brand: BrandSwitcherBrand): string =>
  trimToNull(brand.profileName) ?? brand.sourceDomain;

export const getBrandSwitcherSubtitle = (
  brand: BrandSwitcherBrand,
  intl: IntlShape,
): string | null => {
  if (brand.status === "scanned") {
    return trimToNull(brand.tagline);
  }

  return intl.formatMessage(brandSwitcherStatusMessages[brand.status]);
};

export const getBrandSwitcherLogoUrl = (brand: BrandSwitcherBrand): string | null =>
  trimToNull(brand.logoUrl);

export const getActiveSidebarBrand = <TBrand extends BrandSwitcherBrandWithId>(input: {
  brands: readonly TBrand[];
  routeBrandId: string | undefined;
  contextBrandId: string | undefined;
}): TBrand | null => {
  const brandId = input.routeBrandId ?? input.contextBrandId;
  return input.brands.find((brand) => brand.id === brandId) ?? input.brands[0] ?? null;
};

export const getSidebarBrandContext = <TBrand extends BrandSwitcherBrandWithId>(input: {
  brands: readonly TBrand[];
  routeBrandId: string | undefined;
  contextBrandId: string | undefined;
}): { currentBrand: TBrand | null; sidebarBrandId: string | undefined } => {
  const currentBrand = getActiveSidebarBrand(input);
  return {
    currentBrand,
    sidebarBrandId: currentBrand?.id,
  };
};
